import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OptionSelect, RefSelect } from "@/components/data/FieldInput";
import { SimpleTable } from "@/components/data/SimpleTable";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtNum } from "@/lib/api";

const LIEUX = [{ value: "depot", label: "Par dépôt" }, { value: "superviseur", label: "Par superviseur" }, { value: "revendeur", label: "Par revendeur" }];
const REF = { depot: "stock_depots", superviseur: "superviseurs", revendeur: "revendeurs" };

export function Synthese({ reloadKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/stocks/synthese").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [reloadKey]);
  const alertes = rows.filter((r) => r.alerte);
  return (
    <div className="space-y-3">
      {alertes.map((a) => (
        <div key={a.id} className="flex gap-2 items-center p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid={`stock-alerte-${a.id}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />Stock minimum atteint : <b>{a.article}</b> — {fmtNum(a.depots)} {a.unite} en dépôts (minimum {fmtNum(a.seuil)})
        </div>
      ))}
      <SimpleTable title="Stock théorique par article" module="stocks" testId="stock-synthese-table" rows={rows.map((r) => ({ ...r, etat: r.alerte ? "alerte" : "ok", niveau: r.alerte ? "ecart" : "" }))} cols={[
        { k: "article", label: "Article" }, { k: "unite", label: "Unité" }, { k: "depots", label: "Dépôts KGD", type: "money" },
        { k: "superviseurs", label: "Chez superviseurs", type: "money" }, { k: "revendeurs", label: "Chez revendeurs", type: "money" },
        { k: "total", label: "Total", type: "money" }, { k: "seuil", label: "Minimum", type: "money" }]} />
    </div>
  );
}

export function Consultation({ reloadKey }) {
  const [p, setP] = useState({ lieu_type: "depot", lieu_id: null, superviseur_id: null });
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/stocks/etat", { params: { lieu_type: p.lieu_type, lieu_id: p.lieu_id || "", superviseur_id: p.superviseur_id || "" } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p, reloadKey]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="w-full sm:w-48"><OptionSelect options={LIEUX} value={p.lieu_type} onChange={(v) => v && setP({ lieu_type: v, lieu_id: null, superviseur_id: null })} testId="stock-etat-lieu-type" /></div>
        <div className="w-full sm:w-72"><RefSelect key={p.lieu_type} field={{ name: "lieu_id", type: "ref", ref: REF[p.lieu_type] }} value={p.lieu_id} allowAll placeholder="Tous" onChange={(v) => setP({ ...p, lieu_id: v })} testId="stock-etat-lieu" /></div>
        {p.lieu_type === "revendeur" && (
          <div className="w-full sm:w-72"><RefSelect field={{ name: "superviseur_id", type: "ref", ref: "superviseurs" }} value={p.superviseur_id} allowAll placeholder="Réseau : tous superviseurs" onChange={(v) => setP({ ...p, superviseur_id: v })} testId="stock-etat-reseau" /></div>
        )}
      </div>
      <SimpleTable title={`Stock ${LIEUX.find((l) => l.value === p.lieu_type).label.toLowerCase()}`} module="stocks" testId="stock-etat-table" rows={rows} cols={[
        { k: "lieu", label: "Lieu" }, { k: "article", label: "Article" }, { k: "entrees", label: "Entrées", type: "money" },
        { k: "sorties", label: "Sorties", type: "money" }, { k: "quantite", label: "Stock théorique", type: "money" }]} />
      <p className="text-[11px] text-muted-foreground">Stock théorique = cumul des mouvements validés (les annulations sont contre-passées). Lignes en rouge : stock négatif autorisé.</p>
    </div>
  );
}

export function InventaireLignes({ row, onSaved }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const edit = row.statut === "brouillon" && can("stocks", "modifier");
  useEffect(() => {
    api.get(`/stocks/inventaires/${row.id}/lignes`).then((r) => setRows(r.data.rows)).catch(() => {});
  }, [row.id]);
  const set = (i, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, compte: v, ecart: v === "" ? null : Number(v) - r.theorique } : r)));
  const save = async () => {
    try {
      await api.put(`/stocks/inventaires/${row.id}/lignes`, { lignes: rows.map((r) => ({ article_id: r.article_id, compte: r.compte })) });
      toast.success("Comptage enregistré");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-8" data-testid="inventaire-lignes">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-wider">Comptage physique</h4>
        {edit && <Button size="sm" onClick={save} data-testid="inventaire-save-button"><Save className="h-4 w-4 mr-1" />Enregistrer</Button>}
      </div>
      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="px-2 py-2 text-left">Article</th><th className="px-2 py-2 text-right">Théorique</th><th className="px-2 py-2 text-right">Compté</th><th className="px-2 py-2 text-right">Écart</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.article_id} className={`border-t ${r.ecart ? "bg-red-50" : ""}`}>
                <td className="px-2 py-1.5">{r.article}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtNum(r.theorique)}</td>
                <td className="px-2 py-1.5 text-right">
                  {edit ? <Input type="number" min="0" className="h-7 w-24 ml-auto font-mono text-right" value={r.compte ?? ""} onChange={(e) => set(i, e.target.value)} data-testid={`inventaire-compte-${i}`} /> : <span className="font-mono">{r.compte == null ? "—" : fmtNum(r.compte)}</span>}
                </td>
                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${r.ecart ? "text-primary" : ""}`} data-testid={`inventaire-ecart-${i}`}>{r.ecart == null ? "—" : `${r.ecart > 0 ? "+" : ""}${fmtNum(r.ecart)}`}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="p-3 text-muted-foreground">Aucun article actif</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">À la validation, le théorique est recalculé et chaque écart génère un ajustement validé (traçé dans l'historique).</p>
    </div>
  );
}
